import { useCallback, useContext, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { Button, FormErrorMessage, Input } from '@dashboard/ui'
import { CompanyGame } from '@/api/dashboard'
import { FieldGroup, FormBlock, ImageUploader, ToastSeverity, useToast } from '@/ui'
import { ImageSize } from '@/components/ui/ImageUploader/ValueContainer'
import { AuthContext, IAuthContext } from '@/Context'
import { urlRegexp } from '@/libs'
import { S3Bucket } from '@/types'
import { isAdmin } from '@/security'
import { CompanySettingsFormSkeleton } from '.'
import { FormLayout } from './FormLayout'
import { useQueryClient } from '@tanstack/react-query'
import { dashboardClient, getErrorText } from '@/api'
import { addCompanyToCache, useCompaniesQuery } from '@/api/useCompaniesQuery'
import { useParams } from 'react-router-dom'

export const CompanySettingsForm = () => {
  const context = useContext(AuthContext) as IAuthContext
  const canUserEdit = isAdmin(context.customer)
  const queryClient = useQueryClient()
  const showToast = useToast()
  const { companyId } = useParams() as { companyId: string }
  const { data: dataCompanies, isLoading } = useCompaniesQuery()
  const company = useMemo(() => dataCompanies?.data?.find(c => c.id === companyId), [dataCompanies, companyId])
  const { t } = useTranslation()

  const {
    formState: { isSubmitting, isDirty, errors },
    handleSubmit,
    register,
    control,
    reset,
  } = useForm<CompanyGame>({
    defaultValues: {
      games: [],
      domains: [],
      viewer_domains: [],
      ...company,
    },
  })

  const save = useCallback(
    async (data: CompanyGame) => {
      if (data.logo_url === '') {
        data.logo_url = null as unknown as undefined
      }
      try {
        await dashboardClient.v1.updateCompany(companyId, data)
        addCompanyToCache(queryClient, {
          ...data,
        })
        showToast({ message: t('saved'), severity: ToastSeverity.success })
      } catch (e) {
        showToast({ message: getErrorText(e), severity: ToastSeverity.error })
      }
    },
    [showToast],
  )

  if (isLoading || !company) {
    return <CompanySettingsFormSkeleton />
  }

  const onSubmit = handleSubmit(async data => {
    try {
      const clearData = {
        ...data,
        domains: data.domains?.filter(Boolean),
        viewer_domains: data.viewer_domains?.filter(Boolean),
      }
      await save(clearData)
      reset(clearData)
    } catch (e) {
      console.log(e)
    }
  })

  const onClickDiscard = () => {
    reset(company)
  }

  return (
    <FormLayout
      onSubmit={onSubmit}
      footer={
        <>
          {isDirty && (
            <Button variant="outline" color="secondary" size="sm" onClick={onClickDiscard}>
              {t('Discard')}
            </Button>
          )}
          <Button size="sm" loading={isSubmitting} disabled={!isDirty} type="submit">
            {t('Save')}
          </Button>
        </>
      }
    >
      <FormBlock
        title={t('company-settings-page.general')}
        description={<Trans i18nKey="company-settings-page.general.desc" />}
      >
        <FieldGroup label={t('company-settings-page.name')}>
          <Input
            {...register('name', {
              required: t('validation.required'),
              maxLength: 300,
              setValueAs: value => {
                return value.trim()
              },
            })}
            disabled={!canUserEdit}
            maxLength={300}
          />
          <ErrorMessage
            name="name"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>

        <FieldGroup label={t('company-settings-page.url')}>
          <Input
            {...register('url', {
              required: t('validation.required'),
              pattern: { value: urlRegexp, message: t('validation.invalid_url') },
            })}
            placeholder="https://example.com"
            disabled={!canUserEdit}
          />
          <ErrorMessage
            name="url"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>

        <FieldGroup label={t('company-settings-page.logo_url')}>
          <Controller
            name="logo_url"
            control={control}
            render={({ field }) => (
              <ImageUploader
                imageSize={ImageSize.contain}
                {...field}
                accept={{
                  'image/jpeg': ['.jpeg', '.jpg'],
                  'image/png': [],
                  'image/webp': [],
                  'image/gif': [],
                }}
                bucket={S3Bucket.dashboard}
              />
            )}
          />
        </FieldGroup>
      </FormBlock>
    </FormLayout>
  )
}
